/** Registers the combined Workbox + Push service worker (/sw.js). Notes: docs/code/lib.md. */

import { createElement } from 'react';
import toast from 'react-hot-toast';
import SWUpdateToast, { SW_UPDATE_TOAST_ID } from '@components/SWUpdateToast';
import { reportError } from '@/lib/errors/reportError';
// Side-effect import: updateLifecycle attaches its controllerchange listener and
// the MIN_SUPPORTED_VERSION force path on import. Why here: docs/code/lib.md.
import '@/lib/updateLifecycle';

/**
 * Returns true for transient network failures when the browser tries to fetch
 * sw.js during registration or an explicit update() call.
 *
 * Chrome throws a TypeError with "Failed to update a ServiceWorker" when the
 * script fetch fails at the network level — an unknown error, a DNS blip, or
 * a brief gap during a rolling deploy.  This is the same failure class already
 * suppressed in the periodic registration.update() call below; the browser
 * retries on its own schedule, so there is nothing actionable to report.
 */
export function isSwScriptFetchError(err: unknown): boolean {
  if (!(err instanceof TypeError)) return false;
  const msg = err.message;
  return (
    msg.includes('Failed to update a ServiceWorker') &&
    (msg.includes('An unknown error occurred when fetching the script') ||
      msg.includes('Failed to fetch'))
  );
}

/**
 * Returns true for Chrome's generic "Error: Rejected" thrown when
 * navigator.serviceWorker.register() is rejected at the browser level —
 * e.g. during page unload, after a storage-quota refusal, or when Chrome's
 * internal SW bookkeeping rejects the promise before the script fetch starts.
 * This is a transient, non-actionable browser condition; the browser retries
 * on its own schedule. (GlitchTip #3860)
 */
export function isSwBrowserRejectionError(err: unknown): boolean {
  if (!(err instanceof Error) || err instanceof TypeError) return false;
  return err.message === 'Rejected';
}

/**
 * Fallback prompt for a SW already stuck in `waiting` (installed under the old
 * prompt-based lifecycle, before the SW auto-activated on install — see
 * sw.ts). New versions never enter `waiting` anymore, so this only fires for
 * browsers pinned by a pre-auto-activate SW.
 */
function showUpdatePrompt(worker: ServiceWorker): void {
  const activateUpdate = () => {
    toast.dismiss(SW_UPDATE_TOAST_ID);
    worker.postMessage({ type: 'SKIP_WAITING' });
  };

  toast.custom(
    () => createElement(SWUpdateToast, { onClick: activateUpdate }),
    {
      duration: Infinity,
      id: SW_UPDATE_TOAST_ID,
    },
  );
}

export async function registerSW(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[SW] Service workers not supported');
    return;
  }

  try {
    // Typed as possibly undefined: a browser context that blocks service workers
    // (Playwright `serviceWorkers: 'block'`) resolves register() with nothing.
    const registration = (await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    })) as ServiceWorkerRegistration | undefined;
    if (!registration) {
      console.warn('[SW] register() returned no registration; service workers are blocked here');
      return;
    }

    if (registration.waiting && navigator.serviceWorker.controller) {
      showUpdatePrompt(registration.waiting);
    }

    // Check for a new version hourly and on tab focus/visibility, throttled.
    // Why both: docs/code/lib.md (`swRegistration` update checks).
    let lastUpdateCheck = Date.now();
    const MIN_UPDATE_CHECK_INTERVAL = 60 * 1000;
    const checkForUpdate = () => {
      const now = Date.now();
      if (now - lastUpdateCheck < MIN_UPDATE_CHECK_INTERVAL) return;
      lastUpdateCheck = now;
      // EXPECTED_DOMAIN_ERROR: a failed sw.js fetch is reported at `info`, not
      // dropped (ADR-0007). Rationale: docs/code/lib.md.
      registration.update().catch((err) => {
        reportError(err, 'swRegistration.updateCheck', { level: 'info' });
      });
    };

    setInterval(checkForUpdate, 60 * 60 * 1000);
    window.addEventListener('focus', checkForUpdate);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    });

    // No per-update prompt and no reload on controllerchange here; see
    // docs/code/lib.md (`swRegistration` update checks) and lib/updateLifecycle.

    console.log('[SW] Registered successfully, scope:', registration.scope);
  } catch (error) {
    // Transient network failure fetching sw.js — same class as the periodic
    // registration.update() failure handled below. The browser retries on its
    // own schedule, so log and move on rather than reporting to GlitchTip.
    if (isSwScriptFetchError(error)) {
      console.warn('[SW] Script fetch failed during registration (will retry):', error);
      return;
    }
    // Chrome throws a plain "Error: Rejected" when the SW registration is
    // rejected at the browser level (page unload, storage quota, internal
    // bookkeeping). Transient and non-actionable. (GlitchTip #3860)
    if (isSwBrowserRejectionError(error)) {
      console.warn('[SW] Registration rejected by browser (will retry):', error);
      return;
    }
    reportError(error, 'swRegistration');
  }
}
